/**
 * useTwilioVoice - React hook for browser-based calling via Twilio Voice SDK
 *
 * Manages the Twilio Device lifecycle, outbound/inbound calls,
 * and call state for the softphone dialer UI.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Device } from '@twilio/voice-sdk';
import { config } from '@/src/config';
import { desktopNotifications } from '@/services/desktopNotifications';

// Twilio Voice SDK types
type TwilioDevice = any;
type TwilioCall = any;

interface UseTwilioVoiceOptions {
  /** Auto-register the device on mount */
  autoRegister?: boolean;
}

interface CallerInfo {
  member_id: number;
  member_name: string;
  assigned_to: number | null;
  assigned_name: string | null;
}

interface CallState {
  status: 'idle' | 'connecting' | 'ringing' | 'open' | 'closed' | 'error';
  direction: 'outbound' | 'inbound' | null;
  remoteNumber: string;
  duration: number;
  muted: boolean;
  callSid: string | null;
  callerInfo: CallerInfo | null;
}

export function useTwilioVoice(options: UseTwilioVoiceOptions = {}) {
  const { autoRegister = true } = options;

  const [deviceReady, setDeviceReady] = useState(false);
  const [deviceError, setDeviceError] = useState<string | null>(null);
  const [callState, setCallState] = useState<CallState>({
    status: 'idle',
    direction: null,
    remoteNumber: '',
    duration: 0,
    muted: false,
    callSid: null,
    callerInfo: null,
  });
  const [incomingCall, setIncomingCall] = useState<TwilioCall | null>(null);
  const [identity, setIdentity] = useState<string>('');

  const deviceRef = useRef<TwilioDevice | null>(null);
  const activeCallRef = useRef<TwilioCall | null>(null);
  const durationTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const errorCountRef = useRef<number>(0);

  // Start duration timer when call is open
  const startDurationTimer = useCallback(() => {
    if (durationTimerRef.current) clearInterval(durationTimerRef.current);
    const startTime = Date.now();
    durationTimerRef.current = setInterval(() => {
      setCallState(prev => ({
        ...prev,
        duration: Math.floor((Date.now() - startTime) / 1000),
      }));
    }, 1000);
  }, []);

  const stopDurationTimer = useCallback(() => {
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
  }, []);

  // Fetch voice token and initialize device
  const initDevice = useCallback(async () => {
    try {
      setDeviceError(null);
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      const response = await fetch(
        `${config.weaveinApi.baseUrl}/api/voice-workflows/token/voice`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': cboId || '1',
          },
        }
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({ detail: 'Failed to get voice token' }));
        const msg = err.detail || 'Failed to get voice token';
        // Don't retry if server says it's not configured
        if (response.status === 503) {
          setDeviceError(msg);
          setDeviceReady(false);
          return;
        }
        throw new Error(msg);
      }

      const data = await response.json();
      setIdentity(data.identity);

      // Destroy existing device if any
      if (deviceRef.current) {
        deviceRef.current.destroy();
      }

      const device = new Device(data.token, {
        codecPreferences: ['opus', 'pcmu'],
        enableImprovedSignalingErrorPrecision: true,
      });

      // Device events
      device.on('registered', () => {
        setDeviceReady(true);
        setDeviceError(null);
        errorCountRef.current = 0;
        console.log('Twilio Device registered');
      });

      device.on('error', (error: any) => {
        const code = error?.code || error?.twilioError?.code;
        console.error(`Twilio Device error (code=${code}):`, error);

        // Only count persistent signaling/auth errors, not transient network blips
        if (code === 31005 || code === 20104) {
          errorCountRef.current += 1;
        }
        setDeviceError(error.message || 'Device error');

        // Stop the register/error loop after repeated signaling failures
        if (errorCountRef.current >= 3) {
          console.warn('Twilio Device: repeated signaling errors, destroying device');
          device.destroy();
          deviceRef.current = null;
          setDeviceReady(false);
          setDeviceError('Voice connection failed. Refresh the page to retry.');
        }
      });

      device.on('unregistered', () => {
        setDeviceReady(false);
      });

      device.on('tokenWillExpire', async () => {
        // Refresh token before it expires
        try {
          const refreshResp = await fetch(
            `${config.weaveinApi.baseUrl}/api/voice-workflows/token/voice`,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                'X-CBO-ID': localStorage.getItem('active_cbo_id') || '1',
              },
            }
          );
          const refreshData = await refreshResp.json();
          device.updateToken(refreshData.token);
        } catch (e) {
          console.error('Failed to refresh voice token:', e);
        }
      });

      // Incoming call
      device.on('incoming', async (call: TwilioCall) => {
        const callerNumber = call.parameters.From || '';
        console.log('Incoming call from:', callerNumber);
        setIncomingCall(call);
        setCallState(prev => ({
          ...prev,
          status: 'ringing',
          direction: 'inbound',
          remoteNumber: callerNumber,
          callSid: call.parameters.CallSid || null,
          callerInfo: null,
        }));

        // Play ringtone notification
        try {
          const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQ==');
          audio.loop = true;
          audio.play().catch(() => {});
          call.on('cancel', () => audio.pause());
          call.on('accept', () => audio.pause());
          call.on('reject', () => audio.pause());
          call.on('disconnect', () => audio.pause());
        } catch {}

        // Desktop notification (routes through native OS notifications on Tauri,
        // Web Notifications API in the browser).
        void desktopNotifications.showNotification({
          title: 'Incoming Call',
          body: `Call from ${callerNumber}`,
          icon: '/phone-icon.png',
        });

        // Look up caller info (member name, assigned staff)
        try {
          const infoResp = await fetch(
            `${config.weaveinApi.baseUrl}/api/voice-workflows/inbound-caller-info?phone=${encodeURIComponent(callerNumber)}`
          );
          if (infoResp.ok) {
            const info = await infoResp.json();
            if (info.found) {
              setCallState(prev => ({
                ...prev,
                callerInfo: {
                  member_id: info.member_id,
                  member_name: info.member_name,
                  assigned_to: info.assigned_to,
                  assigned_name: info.assigned_name,
                },
              }));
            }
          }
        } catch (e) {
          console.warn('Failed to look up caller info:', e);
        }

        call.on('cancel', () => {
          setIncomingCall(null);
          setCallState({
            status: 'idle', direction: null, remoteNumber: '',
            duration: 0, muted: false, callSid: null, callerInfo: null,
          });
        });
      });

      // Register the device
      await device.register();
      deviceRef.current = device;

    } catch (error: any) {
      console.error('Failed to init Twilio Device:', error);
      setDeviceError(error.message || 'Failed to initialize voice');
      setDeviceReady(false);
    }
  }, []);

  // Make an outbound call
  const makeCall = useCallback(async (toNumber: string) => {
    if (!deviceRef.current || !deviceReady) {
      throw new Error('Voice device not ready');
    }

    try {
      setCallState({
        status: 'connecting',
        direction: 'outbound',
        remoteNumber: toNumber,
        duration: 0,
        muted: false,
        callSid: null,
      });

      const call = await deviceRef.current.connect({
        params: { To: toNumber },
      });

      activeCallRef.current = call;

      // Call events
      call.on('ringing', () => {
        setCallState(prev => ({ ...prev, status: 'ringing' }));
      });

      call.on('accept', () => {
        setCallState(prev => ({
          ...prev,
          status: 'open',
          callSid: call.parameters?.CallSid || null,
        }));
        startDurationTimer();
      });

      call.on('disconnect', () => {
        stopDurationTimer();
        activeCallRef.current = null;
        setCallState({
          status: 'idle', direction: null, remoteNumber: '',
          duration: 0, muted: false, callSid: null,
        });
      });

      call.on('cancel', () => {
        stopDurationTimer();
        activeCallRef.current = null;
        setCallState({
          status: 'idle', direction: null, remoteNumber: '',
          duration: 0, muted: false, callSid: null,
        });
      });

      call.on('error', (error: any) => {
        console.error('Call error:', error);
        stopDurationTimer();
        activeCallRef.current = null;
        setCallState({
          status: 'error', direction: null, remoteNumber: '',
          duration: 0, muted: false, callSid: null,
        });
      });

      return call;
    } catch (error: any) {
      setCallState({
        status: 'error', direction: null, remoteNumber: '',
        duration: 0, muted: false, callSid: null,
      });
      throw error;
    }
  }, [deviceReady, startDurationTimer, stopDurationTimer]);

  // Accept incoming call
  const acceptCall = useCallback(() => {
    if (!incomingCall) return;

    incomingCall.accept();
    activeCallRef.current = incomingCall;
    setIncomingCall(null);

    setCallState(prev => ({ ...prev, status: 'open' }));
    startDurationTimer();

    incomingCall.on('disconnect', () => {
      stopDurationTimer();
      activeCallRef.current = null;
      setCallState({
        status: 'idle', direction: null, remoteNumber: '',
        duration: 0, muted: false, callSid: null, callerInfo: null,
      });
    });
  }, [incomingCall, startDurationTimer, stopDurationTimer]);

  // Reject incoming call — sends caller to voicemail
  const rejectCall = useCallback(() => {
    if (!incomingCall) return;
    incomingCall.reject();
    setIncomingCall(null);
    setCallState({
      status: 'idle', direction: null, remoteNumber: '',
      duration: 0, muted: false, callSid: null, callerInfo: null,
    });
  }, [incomingCall]);

  // Hang up active call
  const hangUp = useCallback(() => {
    if (activeCallRef.current) {
      activeCallRef.current.disconnect();
      activeCallRef.current = null;
    }
    stopDurationTimer();
    setCallState({
      status: 'idle', direction: null, remoteNumber: '',
      duration: 0, muted: false, callSid: null,
    });
  }, [stopDurationTimer]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (!activeCallRef.current) return;
    const newMuted = !callState.muted;
    activeCallRef.current.mute(newMuted);
    setCallState(prev => ({ ...prev, muted: newMuted }));
  }, [callState.muted]);

  // Send DTMF digit
  const sendDigit = useCallback((digit: string) => {
    if (activeCallRef.current) {
      activeCallRef.current.sendDigits(digit);
    }
  }, []);

  // Auto-register on mount
  useEffect(() => {
    if (autoRegister) {
      initDevice();
    }
    return () => {
      stopDurationTimer();
      if (deviceRef.current) {
        deviceRef.current.destroy();
        deviceRef.current = null;
      }
    };
  }, []);

  return {
    // State
    deviceReady,
    deviceError,
    callState,
    incomingCall: !!incomingCall,
    identity,

    // Actions
    initDevice,
    makeCall,
    acceptCall,
    rejectCall,
    hangUp,
    toggleMute,
    sendDigit,
  };
}
